import clsx from 'clsx'
import { motion } from 'framer-motion'
import { Folder, Star, StarHalf, Shield } from 'lucide-react'
import { useState, useRef, useEffect } from 'react'
import { Link } from 'react-router-dom'

import Badge from './Badge'
import CompareToggleButton from './CompareToggleButton'
import ToolLogo from './ToolLogo'
import { classifyStudentOffer } from '../../utils/student'

const MotionCard = motion.div

// One size for every label chip, and never wrapped mid-label ("Student / Perks").
const CHIP = 'inline-flex items-center gap-1 whitespace-nowrap rounded-full border px-2 py-0.5 text-[11px] font-bold uppercase leading-5 tracking-wider'

const pricingClasses = {
  free: 'bg-accent-soft text-accent-ink',
  freemium: 'bg-bg-sunk text-ink-2',
  paid: 'bg-bg-sunk text-ink-2',
}


// "2026-07-02" -> "Jul 2026". Returns null for missing/unparseable dates so
// the caller can fall back to an unqualified "Hand-tested" chip.
function formatVerifiedShort(iso) {
  if (!iso) return null
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return null
  return date.toLocaleString('en-US', { month: 'short', year: 'numeric' })
}

function slugify(value = '') {
  return value
    .toString()
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
}

function Card({ tool = {}, layoutType = 'standard', glass = false, folders = null, onFoldersUpdated = null }) {
  const [isHovered, setIsHovered] = useState(false)
  const [showFolderDropdown, setShowFolderDropdown] = useState(false)
  const cardRef = useRef(null)
  const rectRef = useRef(null)
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 })

  const handleMouseMove = (e) => {
    if (!cardRef.current) return
    if (!rectRef.current) {
      rectRef.current = cardRef.current.getBoundingClientRect()
    }
    const rect = rectRef.current
    setMousePos({ x: e.clientX - rect.left, y: e.clientY - rect.top })
  }

  useEffect(() => {
    if (!isHovered) return undefined
    const handleScroll = () => {
      rectRef.current = null
    }
    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [isHovered])

  const isLarge = layoutType === 'large'
  // 'wide' used to be a third layoutType, but it was only ever read here and
  // changed nothing about the render — the grid widened the cell and the card
  // inside was identical to a standard one. Removed with the bento grid.
  const isFeatured = Boolean(tool.featured) && !tool.sponsored

  const name = tool.name || 'Unknown Tool'
  const description = tool.shortDescription || tool.description || 'No description available.'
  const category = tool.category || 'coding'
  const rating = Math.max(0, Math.min(5, Number(tool.rating) || 0))
  const reviewCount = Number(tool.review_count ?? tool.reviewCount ?? tool.reviews ?? 0)
  // Only show a star rating when it's backed by real user reviews. Every tool
  // here is hand-picked, so with no reviews we show an honest "Curated" mark
  // instead of a fabricated or empty star widget.
  const hasRealRating = rating > 0 && reviewCount > 0
  const pricing = (tool.pricing || 'free').toLowerCase()
  const slug = tool.slug || slugify(name)
  // Shown instead of a star widget when there are no real reviews. Backed by
  // the actual hand-test date from the catalog, so the chip is a claim we can
  // stand behind.
  const verifiedLabel = formatVerifiedShort(tool.last_verified_at || tool.lastVerifiedAt)

  const ratingStars = Array.from({ length: 5 }, (_, index) => {
    const floor = Math.floor(rating)
    if (index < floor) {
      return (
        <Star
          key={`${name}-star-${index}`}
          className="h-3.5 w-3.5 fill-warn text-warn"
        />
      )
    }
    if (index === floor) {
      const remainder = rating - floor
      if (remainder >= 0.75) {
        return (
          <Star
            key={`${name}-star-${index}`}
            className="h-3.5 w-3.5 fill-warn text-warn"
          />
        )
      }
      if (remainder >= 0.25) {
        return (
          <StarHalf
            key={`${name}-star-${index}`}
            className="h-3.5 w-3.5 fill-warn text-warn"
          />
        )
      }
    }
    return (
      <Star
        key={`${name}-star-${index}`}
        className="h-3.5 w-3.5 text-line-strong"
      />
    )
  })

  const handleToggleFolder = async (folderName, isMember) => {
    try {
      const url = `/api/v1/profile/favorites/folders/${encodeURIComponent(folderName)}/tools${isMember ? '/' + encodeURIComponent(slug) : ''}`
      const method = isMember ? 'DELETE' : 'POST'
      const body = isMember ? undefined : JSON.stringify({ tool_id: slug })
      const headers = isMember ? {} : { 'Content-Type': 'application/json' }

      const res = await fetch(url, {
        method,
        headers,
        body,
        credentials: 'include'
      })

      if (res.ok) {
        if (onFoldersUpdated) {
          onFoldersUpdated()
        }
      }
    } catch (err) {
      console.error('Error toggling folder membership', err)
    }
  }

  // relevance_reason is only set by the personalised recommender (dashboard/syllabus).
  // For regular catalog cards, fall through to the tool's own tagline so the
  // callout is always specific to the tool, not a generic category blurb.
  // If nothing is available the callout is hidden entirely.
  const displayReason = tool.relevance_reason || tool.reason || tool.tagline || null
  const baseMinHeight = isLarge ? 450 : 260

  const isInAnyFolder = folders ? folders.some(f => Array.isArray(f.tools) && f.tools.map(t => String(t).toLowerCase()).includes(String(slug).toLowerCase())) : false

  return (
    <MotionCard
      layout
      ref={cardRef}
      onMouseMove={handleMouseMove}
      onHoverStart={() => setIsHovered(true)}
      onHoverEnd={() => {
        setIsHovered(false)
        setShowFolderDropdown(false)
        rectRef.current = null
      }}
      onFocusCapture={() => setIsHovered(true)}
      onBlurCapture={() => {
        setIsHovered(false)
        setShowFolderDropdown(false)
        rectRef.current = null
      }}
      whileHover={{ y: -4, boxShadow: 'var(--shadow-lg)' }}
      whileTap={{ scale: 0.98 }}
      style={{ minHeight: baseMinHeight }}
      transition={{ type: 'spring', stiffness: 300, damping: 20 }}
      className={clsx(
 "group relative flex w-full flex-col gap-4 overflow-hidden rounded-2xl border text-left shadow-sm cursor-pointer transition-all hover:border-accent focus-within:ring-2 focus-within:ring-accent focus-within:ring-offset-2 focus-within:ring-offset-bg",
        // Editorial emphasis that costs the grid nothing: a featured tool keeps
        // the same footprint as every other card, so the set stays comparable.
        isFeatured ? "border-accent/40" : "border-line",
        glass ? "glass-card bg-opacity-70 hover:bg-opacity-80" : "bg-bg-elev",
        isLarge ? "p-6" : "p-4"
      )}
    >
      <div 
        className="pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-500 ease-in-out group-hover:opacity-100 z-0"
        style={{
          background: `radial-gradient(circle at ${mousePos.x}px ${mousePos.y}px, rgba(150, 150, 150, 0.08), transparent 50%)`,
        }}
      />
      
      <div className="absolute right-2 top-2 z-10 flex items-center gap-1.5" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
        {folders && (
          <div className="relative">
            <button
              type="button"
              onClick={() => setShowFolderDropdown(!showFolderDropdown)}
              aria-label="Manage folders"
              className={clsx(
 'inline-flex h-10 w-10 items-center justify-center rounded-full outline-none transition focus-visible:ring-2 focus-visible:ring-accent md:h-8 md:w-8',
                isInAnyFolder
                  ? 'border border-accent bg-accent/10 text-accent'
                  : 'border border-line bg-bg-elev text-muted hover:border-line-strong hover:text-ink',
              )}
            >
              <Folder className="h-4 w-4" />
            </button>
            {showFolderDropdown && (
              <div
                className="absolute right-0 mt-1.5 z-30 w-48 rounded-xl border border-line bg-bg-elev p-2.5 shadow-xl backdrop-blur-md bg-opacity-95"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="mb-2 px-1 text-xs font-bold uppercase tracking-wider text-muted">Add to Folder</div>
                <div className="space-y-0.5 max-h-40 overflow-y-auto">
                  {folders.length === 0 ? (
                    <p className="text-xs text-muted p-2 text-center">No folders created yet.</p>
                  ) : (
                    folders.map((folder) => {
                       const isMember = Array.isArray(folder.tools) && folder.tools.map(t => String(t).toLowerCase()).includes(String(slug).toLowerCase());
                      return (
                        <label
                          key={folder.name}
                          className="flex items-center gap-2 rounded-lg px-2 py-1.5 hover:bg-bg-sunk cursor-pointer text-xs font-medium text-ink-2"
                        >
                          <input
                            type="checkbox"
                            checked={isMember}
                            onChange={() => handleToggleFolder(folder.name, isMember)}
                            className="rounded border-line-strong text-accent focus:ring-accent accent-accent"
                          />
                          <span className="truncate">{folder.name}</span>
                        </label>
                      );
                    })
                  )}
                </div>
              </div>
            )}
          </div>
        )}
        <CompareToggleButton slug={slug} toolName={name} />
      </div>
 
      <div className={clsx("flex items-start gap-3", isLarge ? "flex-col items-center text-center mt-2" : "")}>
        <div className={clsx("flex shrink-0 items-center justify-center overflow-hidden", isLarge ? "h-20 w-20" : "h-12 w-12")} aria-hidden="true">
          <ToolLogo tool={tool} size={isLarge ? 80 : 48} />
        </div>
 
        <div className={clsx("min-w-0 flex-1", isLarge ? "mt-4 w-full" : "")}>
          {/* Stretched link: the name is a real <a href> whose ::after covers
              the whole card, so the card opens in a new tab, works from the
              keyboard and is crawlable. The folder/compare buttons sit above
              it at z-10, so they still receive their own clicks. */}
          <h3 className={clsx("truncate font-semibold text-ink", isLarge ? "text-xl" : "text-base", !isLarge && (folders ? "pr-20" : "pr-8"))}>
            <Link
              to={`/tools/${slug}`}
              className="outline-none after:absolute after:inset-0 after:z-[1] after:rounded-2xl after:content-['']"
            >
              {name}
            </Link>
          </h3>
          {/* Fixed clamp. This used to grow from 2 lines to 3 on hover, which
              reflowed the card under the user's cursor and pushed the badges
              and pricing chip down mid-interaction. A stable 3 lines shows more
              than the old resting state did, and nothing moves. */}
          <p
            className={clsx("mt-1 overflow-hidden text-muted", isLarge ? "text-base" : "text-sm")}
            style={{
              display: '-webkit-box',
              WebkitLineClamp: isLarge ? 4 : 3,
              WebkitBoxOrient: 'vertical',
            }}
          >
            {description}
          </p>
 
          {displayReason && (
            <div className="mt-2.5 flex items-start gap-1.5 rounded-lg bg-accent-soft/40 px-2.5 py-1.5 text-xs text-ink-2 border border-accent/10 backdrop-blur-sm">
              <span>{displayReason}</span>
            </div>
          )}
        </div>
      </div>
 
      {/* Footer: labels on one wrapping row, then rating and price pinned to
          a ruled bottom row, so every card in a grid row ends on the same
          line whatever mix of labels it has. */}
      <div className={clsx("mt-auto flex w-full flex-col gap-3", isLarge && "items-center")}>
        <div className={clsx("flex flex-wrap items-center gap-1.5", isLarge && "justify-center")}>
          {tool.sponsored && (
            <span
              className={clsx(CHIP, 'border-accent/30 bg-accent-soft text-accent-ink')}
              title="Paid placement — this listing's maker sponsors its spot in this category"
            >
              Sponsored
            </span>
          )}
          {/* Deliberately not called "Featured" — that word is never used for
              paid placement. This one is an editorial pick, and the two must
              never read as the same thing. */}
          {isFeatured && (
            <span
              className={clsx(CHIP, 'border-line-strong bg-bg-sunk text-ink-2')}
              title="Hand-picked by the AI Compass editors — not a paid placement"
            >
              Editor&apos;s pick
            </span>
          )}
          <Badge label={category} variant={category} className="whitespace-nowrap px-2 py-0.5 leading-5" />
          {tool.academic_integrity_rating && (
            <span
              className={clsx(
                CHIP,
                tool.academic_integrity_rating === 'Safe' && 'border-accent/20 bg-accent/10 text-accent-ink',
                tool.academic_integrity_rating === 'Use with Caution' && 'border-warn/20 bg-warn/10 text-warn',
                tool.academic_integrity_rating === 'High Risk' && 'border-danger/20 bg-danger/10 text-danger'
              )}
              title={tool.academic_warning || 'Academic Safety'}
            >
              <Shield className="h-3 w-3" /> {tool.academic_integrity_rating === 'Use with Caution' ? 'Caution' : tool.academic_integrity_rating}
            </span>
          )}
          {(() => {
            const studentTag = classifyStudentOffer(tool)
            return studentTag && (
              <span className={clsx(
                CHIP,
                studentTag === 'Student Hacks'
                  ? 'border-warn/20 bg-warn/10 text-warn'
                  : 'border-accent/20 bg-accent/10 text-accent-ink'
              )}>
                {studentTag}
              </span>
            )
          })()}
        </div>

        <div className={clsx("flex w-full items-center justify-between gap-3 border-t border-line/70 pt-3", isLarge && "justify-center")}>
          {hasRealRating ? (
            <div className="flex items-center gap-0.5" aria-label={`Rated ${rating} out of 5`}>
              {ratingStars}
              <span className="ml-1 text-xs tabular-nums text-muted">({reviewCount})</span>
            </div>
          ) : (
            <span
              className="inline-flex items-center gap-1 text-xs font-medium text-accent-ink"
              aria-label={verifiedLabel ? `Hand-tested ${verifiedLabel}` : 'Hand-tested pick'}
              title={verifiedLabel ? `Last hand-tested ${verifiedLabel}` : undefined}
            >
              <Star className="h-3 w-3 fill-current" />
              {verifiedLabel ? `Verified ${verifiedLabel}` : 'Hand-tested'}
            </span>
          )}

          {tool.pricing && (
            <span
              className={clsx(
                'whitespace-nowrap rounded-full px-2.5 py-0.5 text-[11px] font-semibold uppercase leading-5 tracking-wide',
                pricingClasses[pricing] || pricingClasses.free,
              )}
            >
              {tool.pricing}
            </span>
          )}
        </div>
      </div>
    </MotionCard>
  )
}

export default Card